/**
 * Product service types and interfaces
 */

export interface Product {
  id: string;
  name: string;
  description: string;
  sku: string;
  price: number;
  cost?: number;
  category: ProductCategory;
  subcategory?: string;
  brand?: string;
  images: ProductImage[];
  specifications: ProductSpecification[];
  inventory: ProductInventory;
  status: ProductStatus;
  tags: string[];
  isActive: boolean;
  isFeatured: boolean;
  weight?: number;
  dimensions?: ProductDimensions;
  vendor: ProductVendor;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
}

export interface ProductCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  parentId?: string;
  isActive: boolean;
}

export interface ProductImage {
  id: string;
  url: string;
  alt?: string;
  isPrimary: boolean;
  order: number;
}

export interface ProductSpecification {
  name: string;
  value: string;
  unit?: string;
}

export interface ProductInventory {
  quantity: number;
  reserved: number;
  available: number;
  lowStockThreshold: number;
  trackInventory: boolean;
}

export interface ProductDimensions {
  length: number;
  width: number;
  height: number;
  unit: "cm" | "in" | "m";
}

export interface ProductVendor {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
}

export enum ProductStatus {
  DRAFT = "draft",
  ACTIVE = "active",
  INACTIVE = "inactive",
  DISCONTINUED = "discontinued",
}

export interface CreateProductPayload {
  name: string;
  description: string;
  sku: string;
  price: number;
  cost?: number;
  categoryId: string;
  subcategory?: string;
  brand?: string;
  images?: File[];
  specifications?: ProductSpecification[];
  inventory?: Partial<ProductInventory>;
  tags?: string[];
  weight?: number;
  dimensions?: ProductDimensions;
  vendorId: string;
}

export interface UpdateProductPayload {
  name?: string;
  description?: string;
  sku?: string;
  price?: number;
  cost?: number;
  categoryId?: string;
  subcategory?: string;
  brand?: string;
  images?: File[];
  specifications?: ProductSpecification[];
  inventory?: Partial<ProductInventory>;
  status?: ProductStatus;
  tags?: string[];
  weight?: number;
  dimensions?: ProductDimensions;
  vendorId?: string;
  isActive?: boolean;
  isFeatured?: boolean;
}

export interface ProductSearchParams {
  query?: string;
  /** Public products list search (API query param `search`) */
  search?: string;
  categoryId?: string;
  categoryPath?: string;
  brand?: string;
  minPrice?: number;
  maxPrice?: number;
  status?: ProductStatus;
  isActive?: boolean;
  isFeatured?: boolean;
  tags?: string[];
  vendorId?: string;
  page?: number;
  limit?: number;
  sortBy?: "name" | "price" | "createdAt" | "updatedAt";
  sortOrder?: "asc" | "desc";
  // Filter parameters
  priceMin?: number;
  priceMax?: number;
  supplierLocation?: string[];
  verifiedOnly?: boolean;
  supplierRating?: number;
  moqMin?: number;
  moqMax?: number;
  productCondition?: string[];
  certifications?: string[];
  tradeAssurance?: boolean;
  freeSamples?: boolean;
  customizationAvailable?: boolean;
  fastShipping?: boolean;
  onTimeDeliveryRate?: number;
  /**
   * Public `GET /products/category/:id` only: when true, API includes products in descendant categories (query `includeDescendants=true`).
   */
  includeDescendants?: boolean;
}

export interface ProductResponse {
  product: Product;
  message: string;
  success: boolean;
}

export interface ProductsResponse {
  products: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  message: string;
  success: boolean;
}

export interface ProductCategoriesResponse {
  categories: ProductCategory[];
  message: string;
  success: boolean;
}

export interface ProductSearchResponse {
  products: Product[];
  suggestions: string[];
  filters: {
    categories: Array<{ id: string; name: string; count: number }>;
    brands: Array<{ name: string; count: number }>;
    priceRange: { min: number; max: number };
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  message: string;
  success: boolean;
}

export interface BulkUpdatePayload {
  productIds: string[];
  updates: Partial<UpdateProductPayload>;
}

export interface BulkDeletePayload {
  productIds: string[];
}

export interface ProductUploadResponse {
  images: ProductImage[];
  message: string;
  success: boolean;
}

export interface FeaturedProductsResponse {
  products: Product[];
  message: string;
  success: boolean;
}

export interface TopDealsResponse {
  products: Product[];
  pagination?: {
    total: number;
    limit: number;
  };
  message: string;
  success: boolean;
}

export interface TopRankingResponse {
  products: Product[];
  pagination?: {
    total: number;
    limit: number;
  };
  message: string;
  success: boolean;
}

export interface NewArrivalsResponse {
  products: Product[];
  pagination?: {
    total: number;
    limit: number;
  };
  message: string;
  success: boolean;
}

export interface PopularSuggestionsResponse {
  suggestions: string[];
  message: string;
  success: boolean;
}

/** Single item for hero "frequently searched" (category/term with optional image and slug). */
export interface FrequentlySearchedItemDto {
  categoryName: string;
  imageUrl?: string;
  slug?: string;
}

export interface FrequentlySearchedResponse {
  items: FrequentlySearchedItemDto[];
  message?: string;
  success?: boolean;
}

/** Single hero promo item (title + CTA). */
export interface HeroPromoItemDto {
  title: string;
  ctaText: string;
  ctaUrl?: string;
}

export interface HeroPromoItemsResponse {
  items: HeroPromoItemDto[];
  message?: string;
  success?: boolean;
}
